import React, { useState } from 'react';
import { FileCode2, Plus, Clock, Edit2, Check, X, LogOut, Trash2, FolderOpen } from 'lucide-react';
import { type Project, type ProjectFile, updateFile, softDeleteFile } from '../../../../lib/apis_vibe';
import { useAuth } from '../../../../contexts/AuthContext';

interface FileGridProps {
  project: Project | null;
  loading: boolean;
  onOpenFile: (file: ProjectFile) => void;
  onCreateFile: () => void;
  onFileUpdated?: () => void;
  onFileDeleted?: () => void;
}

const FileGrid: React.FC<FileGridProps> = ({ 
  project, loading, onOpenFile, onCreateFile, onFileUpdated, onFileDeleted
}) => {
  const { logout } = useAuth();

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const startEditing = (e: React.MouseEvent, file: ProjectFile) => {
    e.stopPropagation();
    setEditingId(file.id);
    setEditName(file.name);
  };

  const cancelEditing = (e?: React.SyntheticEvent) => {
    e?.stopPropagation();
    setEditingId(null);
    setEditName('');
  };

  const handleRename = async (e: React.SyntheticEvent, fileId: number) => {
    e.stopPropagation();
    if (!editName.trim()) return;

    try {
      let newName = editName.trim();
      if (!newName.endsWith('.html')) newName += '.html';

      await updateFile(fileId, { name: newName });
      setEditingId(null);
      if (onFileUpdated) onFileUpdated();
    } catch (err) {
      alert("重命名失败");
    }
  };

  const handleDeleteFile = async (fileId: number) => {
    setIsDeleting(true);
    try {
      await softDeleteFile(fileId);
      setDeleteConfirmId(null);
      if (onFileDeleted) onFileDeleted();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || "删除失败";
      alert(msg);
      setDeleteConfirmId(null);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!project) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-gray-500 relative">
        <div className="absolute top-6 right-6">
          <button 
            onClick={logout}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-400 hover:text-red-400 hover:bg-white/5 rounded-lg transition-colors"
          >
            <LogOut size={16} />
            退出
          </button>
        </div>

        <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mb-4">
          <FolderOpen size={32} className="opacity-20" />
        </div>
        <p>请从左侧选择一个项目</p>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-[#0c0c0e] p-8 overflow-y-auto">
      <div className="flex items-end justify-between mb-8 border-b border-white/10 pb-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">{project.name}</h1>
          <p className="text-gray-400 text-sm max-w-2xl">
            {project.description || "暂无描述"}
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          <button 
            onClick={onCreateFile}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white px-4 py-2.5 rounded-xl font-bold text-sm transition-all shadow-lg shadow-purple-900/20 active:scale-95"
          >
            <Plus size={18} />
            新建文件
          </button>

          <button 
            onClick={logout}
            className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-400 hover:text-red-400 hover:bg-white/5 rounded-xl transition-colors border border-white/5 hover:border-red-500/30"
            title="退出登录"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-gray-500">加载文件中...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {project.files && project.files.length > 0 ? (
            project.files.map(file => (
              <div 
                key={file.id}
                onClick={() => onOpenFile(file)}
                className="group bg-[#18181b] border border-white/5 hover:border-purple-500/50 rounded-xl p-5 cursor-pointer transition-all hover:shadow-xl hover:shadow-purple-900/10 hover:-translate-y-1 relative overflow-hidden flex items-center gap-4"
              >
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-white/5 to-transparent rounded-bl-full -mr-4 -mt-4 transition-opacity opacity-0 group-hover:opacity-100"></div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 relative z-10">
                      {file.oss_url && (
                        <span className="text-[10px] border border-green-500/30 text-green-400 px-1.5 py-0.5 rounded uppercase font-bold tracking-wider">
                          Published
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-1 relative z-10">
                      <button 
                        onClick={(e) => startEditing(e, file)}
                        className="p-1.5 text-gray-500 hover:text-white hover:bg-white/10 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        title="重命名"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmId(file.id);
                        }}
                        className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-400/10 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        title="删除文件"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  
                  {editingId === file.id ? (
                    <div 
                      className="flex items-center gap-1 mb-2 relative z-20" 
                      onClick={e => e.stopPropagation()}
                    >
                      <input 
                        type="text" 
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        className="w-full bg-black/50 border border-purple-500/50 rounded px-2 py-1 text-sm text-white outline-none"
                        autoFocus
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleRename(e, file.id);
                          if (e.key === 'Escape') cancelEditing(e);
                        }}
                      />
                      <button onClick={e => handleRename(e, file.id)} className="text-green-400 hover:bg-white/10 p-1 rounded"><Check size={14}/></button>
                      <button onClick={e => cancelEditing(e)} className="text-red-400 hover:bg-white/10 p-1 rounded"><X size={14}/></button>
                    </div>
                  ) : (
                    <h3 className="text-gray-200 font-bold mb-2 truncate group-hover:text-purple-300 transition-colors">
                      {file.name}
                    </h3>
                  )}

                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Clock size={12} />
                    <span>{new Date(file.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>

                <div className="w-24 h-24 bg-blue-500/10 text-blue-400 rounded-lg flex items-center justify-center group-hover:bg-blue-500 group-hover:text-white transition-colors overflow-hidden flex-shrink-0">
                  {file.latest_version_img_url ? (
                    <img 
                      src={file.latest_version_img_url} 
                      alt={file.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <FileCode2 size={32} />
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full py-10 flex flex-col items-center justify-center border-2 border-dashed border-white/10 rounded-xl text-gray-500">
              <p className="mb-4">这个项目还是空的</p>
              <button 
                onClick={onCreateFile}
                className="text-purple-400 hover:text-purple-300 underline text-sm"
              >
                创建第一个网页文件
              </button>
            </div>
          )}
        </div>
      )}

      {deleteConfirmId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-[#18181b] border border-red-500/50 rounded-xl p-6 w-full max-w-sm shadow-2xl shadow-red-500/20 animate-in fade-in zoom-in duration-200">
            <div className="flex flex-col items-center text-center mb-6">
              <div className="text-5xl mb-3">😱🗑️</div>
              <h3 className="text-xl font-bold text-red-500 mb-2">确认删除文件？</h3>
              <p className="text-sm text-gray-400">
                文件的所有版本也将被删除，此操作<span className="text-red-500 font-bold">不可恢复</span>！
              </p>
            </div>
            
            <div className="flex flex-col gap-3">
              <button 
                onClick={() => handleDeleteFile(deleteConfirmId)}
                disabled={isDeleting}
                className="w-full py-3 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white rounded-lg font-bold text-sm transition-all shadow-lg flex items-center justify-center gap-2 border border-red-400"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    删除中...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    确认删除
                  </>
                )}
              </button>
              
              <button 
                onClick={() => setDeleteConfirmId(null)}
                disabled={isDeleting}
                className="w-full py-3 bg-white/10 hover:bg-white/20 disabled:opacity-50 text-white rounded-lg font-medium text-sm transition-all"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileGrid;
